import React, { useState } from 'react';
import cn from 'classnames';
import {
  attributesForContactInfo,
  defaultLabelForSource,
  defaultLabelOptionsForSource,
} from '@repo/utils/models/Contact';
import { DropdownMenuOption, ID } from '@repo/utils/types';
import {
  ActionCopyIcon,
  ChevronRightIcon,
  EmailSourceIcon,
  PhoneIcon,
  SpeechBubbleIcon,
} from '@repo/icons';
import { DropdownMenu } from '@repo/components/menus';
import { EllipsisToXIcon } from '@repo/icons/EllipsisToXIcon';
import SourceLabelEditor from '../LabelEditor';
import styles from './ActionsMenu.module.scss';
import SourceLabelOption from '../SourceLabelOption';
import { useDispatch } from 'react-redux';
import { ContactSource } from '@repo/redux/reducers/data/sources';
import { updateSourceLabel } from '@repo/redux/actions/sources';
import { alertUser } from '@repo/redux/actions/alerts';
import { useLocation } from 'react-router-dom';

export default function SourceActionsMenu({
  contactID,
  source,
  triggerClassName,
  isShared,
}: {
  contactID: ID;
  source: ContactSource;
  triggerClassName: string;
  isShared?: boolean;
}) {
  const dispatch = useDispatch();
  const { label, href } = attributesForContactInfo(source, true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingNewLabel, setEditingNewLabel] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const location = useLocation();
  const isSharedContact = isShared || location.pathname.includes('/share/');

  const defaultOptions = defaultLabelOptionsForSource(source.type).map((defaultLabelOption) => {
    return {
      title: (
        <SourceLabelOption selected={label === defaultLabelOption}>
          {defaultLabelOption}
        </SourceLabelOption>
      ),
      action: () => {
        if (defaultLabelForSource(source.type) === defaultLabelOption) {
          dispatch(updateSourceLabel({ contactID, sourceID: source.id, label: null }));
        } else {
          dispatch(
            updateSourceLabel({ contactID, sourceID: source.id, label: defaultLabelOption }),
          );
        }
      },
      keepMenuOpenOnClick: true,
    };
  });

  const options: DropdownMenuOption[] = [
    !isSharedContact && {
      title: (
        <span className={styles.changeLabelOption}>
          Change label <ChevronRightIcon width="10" height="10" />
        </span>
      ),
      submenu: [
        ...defaultOptions,
        {
          divider: true,
        },
        {
          title: (
            <SourceLabelEditor
              contactID={contactID}
              sourceID={source.id}
              editing={editingNewLabel}
              setEditing={setEditingNewLabel}
              value={newLabel}
              setValue={setNewLabel}
            />
          ),
          keepMenuOpenOnClick: true,
          action: () => {},
          className: styles.sourceLabelEditor,
        },
      ],
    },
    !isSharedContact && {
      divider: true,
    },
    {
      title: 'Copy',
      action: () => {
        navigator.clipboard.writeText(source.value);
        dispatch(alertUser('Copied source to clipboard!', 'success'));
      },
      icon: ActionCopyIcon,
    },
    source.type === 'phone' && {
      title: 'Call',
      action: () => {
        window.location.href = `tel:${source.value}`;
      },
      icon: PhoneIcon,
    },
    source.type === 'phone' && {
      title: 'Text',
      action: () => {
        window.location.href = `sms:${source.value}`;
      },
      icon: SpeechBubbleIcon,
    },
    source.type === 'email' && {
      title: 'Email',
      action: () => {
        window.location.href = href;
      },
      icon: EmailSourceIcon,
    },
  ].filter(Boolean) as DropdownMenuOption[];

  return (
    <DropdownMenu
      triggerProps={{ children: <EllipsisToXIcon isOpen={menuOpen} width="12" height="12" /> }}
      options={options}
      suppressFocusControlOnItems
      onClose={() => {
        setEditingNewLabel(false);
        setNewLabel('');
        setMenuOpen(false);
      }}
      onOpen={() => setMenuOpen(true)}
      menuClassName={styles.sourceLabelMenu}
      triggerClassName={cn(
        styles.sourceOverflowMenuTriggerBtn,
        triggerClassName,
        menuOpen && styles.menuOpen,
      )}
    />
  );
}
